import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { SteersCredentialsPageDataQuery } from "generated-graphql/graphql";
import { useState } from "react";
import {
  ADD_STEERS_CREDENTIAL_ACCESS,
  CREATE_ALTERNATE_ID_INFO,
  GET_ALL_CREDENTIALS,
  GET_TENANTS,
} from "./api";
import { isValidCn } from "./utils";

const ENCAMP_TENANT_ID = "e0b7f3e1-a4ab-461b-83a9-7e0f243e6bdf";
const ENCAMP_CREDENTIALS_URL = `/o/${ENCAMP_TENANT_ID}/credentials`;

type Mapping =
  SteersCredentialsPageDataQuery["steersCredentialsPageData"]["mappings"][number];

type Props = {
  existingMappings: Mapping[];
  onClose: () => void;
  onSuccess: () => void;
  /** Pre-select a CN value (disables CN selection UI) */
  initialCnValue?: string;
};

type CredentialOption = {
  id: string;
  username: string;
  name?: string | null;
};

type TenantOption = {
  id: string;
  name: string;
};

export function AddCredentialAccessDialog({
  existingMappings,
  onClose,
  onSuccess,
  initialCnValue,
}: Props): JSX.Element {
  const alerts = useAlerts();

  const [selectedCredential, setSelectedCredential] =
    useState<CredentialOption | null>(null);
  const [selectedCn, setSelectedCn] = useState<string | null>(
    initialCnValue ?? null
  );
  const [isNewCn, setIsNewCn] = useState(false);
  const [newCnValue, setNewCnValue] = useState("");
  const [newCnDescription, setNewCnDescription] = useState("");
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(
    null
  );
  const [credentialSearch, setCredentialSearch] = useState("");

  const [
    fetchCredentials,
    { data: credentialsData, loading: loadingCredentials },
  ] = useLazyQuery(GET_ALL_CREDENTIALS);

  const { data: tenantsData } = useQuery(GET_TENANTS);

  const [addAccess, { loading: addingAccess }] = useMutation(
    ADD_STEERS_CREDENTIAL_ACCESS
  );

  const [createAlternateIdInfo, { loading: creatingCn }] = useMutation(
    CREATE_ALTERNATE_ID_INFO
  );

  const allCredentials = credentialsData?.credentials.items ?? [];
  const tenants = tenantsData?.tenants.items ?? [];
  const existingCns = existingMappings.map(
    (m) => m.alternateIdInfo.alternateIdValue
  );

  const cnValue = isNewCn ? newCnValue : selectedCn;

  // Get credential IDs that already have access to the selected CN
  const existingCredentialIds = new Set(
    existingMappings.find((m) => m.alternateIdInfo.alternateIdValue === cnValue)
      ?.associatedCredentialIds ?? []
  );

  // Filter out credentials that already have access
  const credentials = allCredentials.filter(
    (c) => !existingCredentialIds.has(c.id)
  );
  const isValidNewCn = !isNewCn || (isValidCn(newCnValue) && selectedTenant);
  const canSave =
    selectedCredential && cnValue && (isNewCn ? isValidNewCn : true);

  async function handleSave(): Promise<void> {
    if (!selectedCredential || !cnValue) return;

    try {
      // If creating a new CN, create the AlternateIdInfo first
      if (isNewCn && selectedTenant) {
        await createAlternateIdInfo({
          variables: {
            input: {
              alternateIdValue: newCnValue,
              tenantId: selectedTenant.id,
              description: newCnDescription,
            },
          },
        });
      }

      // Add the credential access
      await addAccess({
        variables: {
          credentialId: selectedCredential.id,
          alternateIdValue: cnValue,
        },
      });

      alerts.success("Credential access added successfully");
      onSuccess();
    } catch (error) {
      alerts.error("Failed to add credential access", error);
    }
  }

  function handleCredentialSearchChange(value: string): void {
    setCredentialSearch(value);
    if (value.length >= 2) {
      fetchCredentials({ variables: { search: value } });
    }
  }

  return (
    <Dialog
      open
      title="Add Credential Access"
      onClose={onClose}
      primaryActionButtonProps={{
        onClick: handleSave,
        disabled: !canSave,
        saveText: "Add Access",
        loading: addingAccess || creatingCn,
      }}
    >
      <Stack spacing={3} sx={{ minWidth: 400 }}>
        <Autocomplete
          options={credentials}
          getOptionLabel={(option) =>
            option.name
              ? `${option.username} (${option.name})`
              : option.username
          }
          value={selectedCredential}
          onChange={(_, value) => setSelectedCredential(value)}
          inputValue={credentialSearch}
          onInputChange={(_, value) => handleCredentialSearchChange(value)}
          loading={loadingCredentials}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Credential"
              placeholder="Search by username..."
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCredentials ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Alert
          severity="info"
          action={
            <Button
              component="a"
              href={ENCAMP_CREDENTIALS_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              startIcon={<OpenInNew sx={{ fontSize: 18 }} />}
            >
              Open Credentials
            </Button>
          }
        >
          Add new credentials to the "Encamp [TEST]" tenant
        </Alert>

        {initialCnValue ? (
          <TextField label="CN" value={initialCnValue} disabled />
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={isNewCn}
                  onChange={(e) => setIsNewCn(e.target.checked)}
                />
              }
              label="Create new CN"
            />

            {isNewCn ? (
              <>
                <TextField
                  label="CN Value"
                  value={newCnValue}
                  onChange={(e) => setNewCnValue(e.target.value.toUpperCase())}
                  placeholder="CN123456789"
                  required
                  error={newCnValue.length > 0 && !isValidCn(newCnValue)}
                  helperText={
                    newCnValue.length > 0 && !isValidCn(newCnValue)
                      ? "CN must start with 'CN'"
                      : ""
                  }
                />
                <Autocomplete
                  options={tenants}
                  getOptionLabel={(option) => option.name}
                  value={selectedTenant}
                  onChange={(_, value) => setSelectedTenant(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Tenant" required />
                  )}
                />
                <TextField
                  label="Description"
                  value={newCnDescription}
                  onChange={(e) => setNewCnDescription(e.target.value)}
                  placeholder="STEERS Company Name (optional)"
                />
              </>
            ) : (
              <Autocomplete
                options={existingCns}
                value={selectedCn}
                onChange={(_, value) => setSelectedCn(value)}
                renderInput={(params) => (
                  <TextField {...params} label="CN" required />
                )}
              />
            )}
          </>
        )}
      </Stack>
    </Dialog>
  );
}
